/**
 * Jobs list — the org's jobs, each row opening the existing job detail (profitability). Reached from
 * Home -> More -> Jobs. Fetches GET /api/jobs (real round-trip) via TanStack Query with loading /
 * empty / error states; rows navigate to jobs/[id].
 *
 * NEVER imports @smartbox/* (mobile invariant 1). Design-system primitives only; no raw styles.
 */
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { listJobs, type JobRow } from '../../../src/api/jobs';
import { Screen, Text, Badge, Card, Button, ListRow } from '../../../src/components/ui';
import type { StatusKey } from '../../../src/theme';
import { theme } from '../../../src/theme';
import { extractErrorMessage, humanize } from '../../../src/lib/format';

function jobBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'scheduled':
    case 'in_progress':
      return { key: 'inProgress', label: humanize(status) };
    case 'completed':
    case 'closed':
      return { key: 'paid', label: humanize(status) };
    case 'cancelled':
    case 'on_hold':
      return { key: 'overdue', label: humanize(status) };
    case 'lead':
      return { key: 'sent', label: humanize(status) };
    default:
      return { key: 'draft', label: humanize(status) || 'Job' };
  }
}

export function JobsListScreen() {
  const router = useRouter();
  const jobsQuery = useQuery({ queryKey: ['jobs-list'], queryFn: listJobs, staleTime: 30_000 });

  if (jobsQuery.isLoading) {
    return (
      <Screen>
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={theme.color.action.primary} accessibilityLabel="Loading jobs" />
        </View>
      </Screen>
    );
  }

  if (jobsQuery.isError) {
    return (
      <Screen>
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(jobsQuery.error, 'Failed to load jobs.')}
          </Text>
          <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => jobsQuery.refetch()} />
        </Card>
      </Screen>
    );
  }

  const jobs = jobsQuery.data ?? [];

  return (
    <Screen scroll>
      <Text variant="title">Jobs</Text>
      <Text variant="label" color="secondary">
        {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'}
      </Text>
      <Button
        title="+ New service call"
        onPress={() => router.push('/(app)/schedule/new?type=service_call' as never)}
        accessibilityLabel="New service call"
      />
      {jobs.length === 0 ? (
        <Card>
          <Text variant="body" color="secondary">
            No jobs yet. Start a service call above, or convert an approved estimate.
          </Text>
        </Card>
      ) : (
        <View style={styles.list}>
          {jobs.map((j: JobRow) => {
            const b = jobBadge(j.status);
            const title = j.name || j.jobNumber || j.id.slice(0, 8).toUpperCase();
            const subtitle = [j.customerName, j.jobNumber ? `#${j.jobNumber}` : null].filter(Boolean).join(' · ');
            return (
              <ListRow
                key={j.id}
                title={title}
                subtitle={subtitle || humanize(j.status)}
                right={<Badge status={b.key} label={b.label} />}
                onPress={() => router.push(`/(app)/jobs/${j.id}`)}
                accessibilityLabel={`Open job ${title}`}
              />
            );
          })}
        </View>
      )}
    </Screen>
  );
}

const styles = StyleSheet.create({
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: theme.spacing.md },
  list: { gap: theme.spacing.sm },
});
